'use client';

import React, { useState, useMemo, useEffect } from 'react';
import PuppyCard from './PuppyCard';
import SearchBar from './SearchBar';
import SortControls from './SortControls';
import InquiryModal from './InquiryModal';
import PuppyFilterInterface from '@/components/common/PuppyFilterInterface';

interface PuppyFilters {
  variety?: string;
  gender?: string;
  ageRange?: string;
  priceRange?: [number, number];
}

// Mock data matching home page FeaturedPuppies
const mockPuppies = [
  {
    id: '1',
    name: 'FINN',
    variety: 'Yellow Labrador',
    age: '10 weeks old',
    ageInWeeks: 10,
    gender: 'Male',
    price: 1800,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/finn_00.jpg',
        alt: 'Beautiful yellow Labrador puppy playing with pink rope toy',
      },
    ],
    description:
      'FINN is a sweet and playful yellow Labrador puppy with a gentle temperament. He loves to play and is great with children. He comes from champion bloodlines and has been raised in our home with lots of love and socialization.',
    weight: '15 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-25',
  },
  {
    id: '2',
    name: 'JAX',
    variety: 'Black Labrador',
    age: '12 weeks old',
    ageInWeeks: 12,
    gender: 'Male',
    price: 1900,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/Jax_00-1769774272160.jpg',
        alt: 'Handsome black Labrador puppy with shiny coat lying on wooden floor',
      },
    ],
    description:
      'JAX is a confident and intelligent black Labrador with a beautiful shiny coat. He is very trainable and eager to please. Perfect for families looking for an active and loyal companion.',
    weight: '18 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-24',
  },
  {
    id: '3',
    name: 'CoCo',
    variety: 'Yellow Labrador',
    age: '9 weeks old',
    ageInWeeks: 9,
    gender: 'Female',
    price: 2000,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/Coco_00-1769774357121.jpg',
        alt: 'Adorable yellow Labrador puppy in spring garden setting',
      },
    ],
    description:
      'CoCo is an adorable and affectionate puppy with a wonderful personality. She loves cuddles and is very social with both people and other pets. She will make a perfect addition to any loving family.',
    weight: '14 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-26',
  },
  {
    id: '4',
    name: 'LuLu',
    variety: 'Chocolate Labrador',
    age: '11 weeks old',
    ageInWeeks: 11,
    gender: 'Female',
    price: 2100,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/Lulu_00-1769774475882.jpg',
        alt: 'Adorable chocolate Labrador puppy on pink blanket',
      },
    ],
    description:
      'LuLu is a stunning chocolate Labrador with a vibrant coat and energetic personality. She is very playful and loves outdoor activities. Great for active families who enjoy spending time outdoors.',
    weight: '17 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-23',
  },
  {
    id: '5',
    name: 'Bonley',
    variety: 'Chocolate Labrador',
    age: '8 weeks old',
    ageInWeeks: 8,
    gender: 'Male',
    price: 1750,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/Bonley_00-1769774580806.jpg',
        alt: 'Playful chocolate Labrador puppy with light brown coat sitting in garden',
      },
    ],
    description:
      'Bonley is a gentle and sweet chocolate Labrador puppy with a beautiful coat. He is calm and loving, perfect for families with young children. He has been well-socialized and is ready for his forever home.',
    weight: '13 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-27',
  },
  {
    id: '6',
    name: 'LiLi',
    variety: 'Yellow Labrador',
    age: '13 weeks old',
    ageInWeeks: 13,
    gender: 'Female',
    price: 1850,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/Lili_00-1769774668713.jpg',
        alt: 'Yellow Labrador puppy with toys on green mat',
      },
    ],
    description:
      'LiLi is a friendly and outgoing yellow Labrador who loves to play and explore. She is very social and gets along well with other dogs. She will bring joy and laughter to any household.',
    weight: '19 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-22',
  },
  {
    id: '7',
    name: 'BRUNO',
    variety: 'Yellow Labrador',
    age: '10 weeks old',
    ageInWeeks: 10,
    gender: 'Male',
    price: 1950,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/Bruno_00-1769774753991.jpg',
        alt: 'Yellow Labrador puppy on blue checkered blanket',
      },
    ],
    description:
      'BRUNO is a handsome yellow Labrador with a wonderful temperament. He is intelligent, easy to train, and loves to be around people. He will make an excellent family companion and is ready to join his new home.',
    weight: '16 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-21',
  },
  {
    id: '8',
    name: 'WALTERS',
    variety: 'Chocolate Labrador',
    age: '9 weeks old',
    ageInWeeks: 9,
    gender: 'Male',
    price: 2050,
    availability: 'available' as const,
    images: [
      {
        url: '/assets/images/Walters_00-1769775257641.jpg',
        alt: 'Chocolate Labrador puppy on colorful plaid blanket',
      },
    ],
    description:
      'WALTERS is a beautiful chocolate Labrador with a striking appearance and friendly personality. He is playful, affectionate, and loves attention. He comes from excellent lineage and will make a wonderful addition to any family.',
    weight: '14 lbs',
    parentInfo: 'Champion bloodline parents',
    healthCertifications: [
      'Vet health check completed',
      'First vaccinations administered',
      'Dewormed and microchipped',
      'Health certificate included',
    ],
    addedDate: '2026-01-20',
  },
];

const AvailablePuppiesInteractive = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<PuppyFilters>({});
  const [sortBy, setSortBy] = useState('newest');
  const [selectedPuppy, setSelectedPuppy] = useState<{ id: string; name: string } | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  const filteredAndSortedPuppies = useMemo(() => {
    let result = [...mockPuppies];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (puppy) =>
          puppy.name.toLowerCase().includes(query) ||
          puppy.variety.toLowerCase().includes(query) ||
          puppy.description.toLowerCase().includes(query)
      );
    }

    // Apply variety filter
    if (filters.variety) {
      result = result.filter((puppy) =>
        puppy.variety.toLowerCase().includes(filters.variety!.toLowerCase())
      );
    }

    // Apply gender filter
    if (filters.gender) {
      result = result.filter(
        (puppy) => puppy.gender.toLowerCase() === filters.gender!.toLowerCase()
      );
    }

    // Apply age range filter
    if (filters.ageRange) {
      const [min, max] = filters.ageRange.split('-').map((n) => parseInt(n));
      if (max) {
        result = result.filter((puppy) => puppy.ageInWeeks >= min && puppy.ageInWeeks <= max);
      } else {
        result = result.filter((puppy) => puppy.ageInWeeks >= min);
      }
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.addedDate).getTime() - new Date(a.addedDate).getTime());
        break;
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'age-asc':
        result.sort((a, b) => a.ageInWeeks - b.ageInWeeks);
        break;
      case 'age-desc':
        result.sort((a, b) => b.ageInWeeks - a.ageInWeeks);
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return result;
  }, [searchQuery, filters, sortBy]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (newFilters: PuppyFilters) => {
    setFilters(newFilters);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
  };

  const handleInquire = (puppyId: string, puppyName: string) => {
    setSelectedPuppy({ id: puppyId, name: puppyName });
  };

  const handleCloseModal = () => {
    setSelectedPuppy(null);
  };

  if (!isHydrated) {
    return (
      <div className="space-y-8">
        <div className="h-12 w-full rounded-md bg-muted" />
        <div className="h-64 w-full rounded-xl bg-muted" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-96 rounded-xl bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Search Bar */}
        <SearchBar onSearch={handleSearch} />

        {/* Filters */}
        <PuppyFilterInterface onFilterChange={handleFilterChange} initialFilters={filters} />

        {/* Sort Controls */}
        <SortControls
          onSortChange={handleSortChange}
          resultCount={filteredAndSortedPuppies.length}
        />

        {/* Puppies Grid */}
        {filteredAndSortedPuppies.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredAndSortedPuppies.map((puppy) => (
              <PuppyCard key={puppy.id} puppy={puppy} onInquire={handleInquire} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-xl bg-card py-16 text-center shadow-warm">
            <div className="mb-4 text-muted-foreground">
              <svg
                className="mx-auto h-16 w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="mb-2 font-heading text-xl font-semibold text-foreground">
              No Puppies Match Your Search
            </h3>
            <p className="text-muted-foreground">
              Try changing your filters or search terms to see more puppies
            </p>
          </div>
        )}
      </div>

      {/* Inquiry Modal */}
      {selectedPuppy && (
        <InquiryModal
          isOpen={!!selectedPuppy}
          onClose={handleCloseModal}
          puppyName={selectedPuppy.name}
          puppyId={selectedPuppy.id}
        />
      )}
    </>
  );
};

export default AvailablePuppiesInteractive;
